import { format, parseISO } from 'date-fns';
import i18n from 'i18next';
import { parseDateOnly } from '@/lib/safe-date';
import type { ClaimStatus, GrantCurrency, GrantExpenseCategory, GrantRequest, ReimbursementClaim, ReimbursementClaimLite } from '@/types/api';
import type { ChipTone } from '@/components/editorial/StatusChip';
import { FAMILY_GRANT_STATUSES, familyGrantStatus, familyGrantStatusLabel, grantTypeLabel, type FamilyGrantStatus } from '@/lib/grants-display';
import { formatMoney, formatMoneyCents, formatMonthYear, formatPreset } from '@/i18n/intl';
import type { AnyTFunction, TKey } from '@/i18n/msg';

type LooseT = (key: string, params?: Record<string, unknown>) => string;

/** The EN catalog value of a key — the R4 compatibility read behind a token's `label`. */
const english = (key: TKey): string => (i18n.getFixedT('en') as unknown as LooseT)(key);

// ── Expense category tokens ──────────────────────────────────────────────

export type ExpenseCategory = 'travel' | 'home_utilities' | 'rent' | 'other';

/**
 * `labelKey` is what a parent surface renders (`t(token.labelKey)`, #2109
 * R3-B4). `label` is the English compatibility read for files R3 does not
 * convert (the chat receipt dialog's option list until L5b, staff until R4).
 */
export interface ExpenseCategoryToken { labelKey: TKey; readonly label: string; rail: string; tone: ChipTone }

function categoryToken(labelKey: TKey, rail: string, tone: ChipTone): ExpenseCategoryToken {
  return { labelKey, get label() { return english(labelKey); }, rail, tone };
}

/** The creation vocabulary, lockstep with the backend enum (#641). */
export const EXPENSE_CATEGORIES: ExpenseCategory[] = ['travel', 'home_utilities', 'rent', 'other'];

// Consumers read the object ({label, rail, tone}) — chips render StatusChip
// from `tone` (#762); `rail` stays a class for the category dot.
export const EXPENSE_CATEGORY_TOKENS: Record<ExpenseCategory, ExpenseCategoryToken> = {
  travel:         categoryToken('money:category.travel',         'bg-cat-magenta-fg',  'magenta'),
  home_utilities: categoryToken('money:category.home_utilities', 'bg-cat-teal-fg',     'teal'),
  rent:           categoryToken('money:category.rent',           'bg-cat-sky-fg',      'sky'),
  other:          categoryToken('money:category.other',          'bg-chip-neutral-fg', 'neutral'),
};

// Legacy categories persist on old rows (data stays; #641). Render them,
// never offer them. Neutral token — never `undefined` — so an old
// medical_bill row can't throw on a `.rail` read.
const NEUTRAL_RAIL = 'bg-stevens-ink-3/40';
const NEUTRAL_TOKEN: ExpenseCategoryToken = categoryToken('money:category.other', NEUTRAL_RAIL, 'neutral');

export const LEGACY_EXPENSE_CATEGORIES = ['medical_bill', 'insurance_premium', 'copay', 'prescription'] as const;

export const LEGACY_EXPENSE_TOKENS: Record<(typeof LEGACY_EXPENSE_CATEGORIES)[number], ExpenseCategoryToken> = {
  medical_bill:      categoryToken('money:legacyCategory.medical_bill',      NEUTRAL_RAIL, 'neutral'),
  insurance_premium: categoryToken('money:legacyCategory.insurance_premium', NEUTRAL_RAIL, 'neutral'),
  copay:             categoryToken('money:legacyCategory.copay',             NEUTRAL_RAIL, 'neutral'),
  prescription:      categoryToken('money:legacyCategory.prescription',      NEUTRAL_RAIL, 'neutral'),
};

export function expenseCategoryToken(category: string | null | undefined): ExpenseCategoryToken {
  if (!category) return NEUTRAL_TOKEN;
  return EXPENSE_CATEGORY_TOKENS[category as ExpenseCategory]
    ?? LEGACY_EXPENSE_TOKENS[category as keyof typeof LEGACY_EXPENSE_TOKENS]
    ?? NEUTRAL_TOKEN;
}

/** The category as the reader sees it; `t` defaults to the active language. */
export function expenseCategoryLabel(category: string | null | undefined, t: AnyTFunction = i18n.t): string {
  return (t as unknown as LooseT)(expenseCategoryToken(category).labelKey);
}

// ── Currency formatter ──────────────────────────────────────────────────

/**
 * A claim amount. `ReimbursementClaim.amount` arrives in DOLLARS, so this is
 * the one place it becomes cents (#2109 R3-B6) before the locale-aware
 * two-decimal formatter; en output is unchanged ("$342.50", "$40.00").
 */
export function formatUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return formatMoneyCents(Math.round(n * 100));
}

/**
 * A lite claim's amount (DOLLARS, possibly a string) in its grant's currency —
 * the one place a receipt line converts to cents (#2109 R3-B6). Whole amounts
 * drop `.00` ("$40"), as the grant's receipt line always has, so en output is
 * unchanged. `Number(null)` is 0 here exactly as it was at the old call site.
 */
export function claimAmountLabel(
  amount: number | string | null | undefined,
  currency: GrantCurrency = 'USD',
): string {
  return formatMoney(Math.round(Number(amount) * 100), currency);
}

// ── Bill.com visibility ──────────────────────────────────────────────────
//
// The three-tab section row (TRF Grants · Expenses & receipts · Bill.com
// (test)) retired on 2026-09-21: `/bills` is one grant-centered page, so
// `FinancesSection`, `FINANCES_SECTIONS`, `isFinancesSection`,
// `visibleFinancesSections`, `resolveFinancesSection` and
// `DEFAULT_FINANCES_SECTION` all went with it, and `?tab=` is no longer a
// store. Historical `?tab=` links still LAND — the param is simply ignored.
//
// `canSeeBillcom` survives: the BILL routes are still guarded on the roles an
// account HOLDS, and the staff-side surface still reads it. What changed is
// that a family page no longer renders vendor test machinery behind a role
// check (§1.9, "honest about what it is").

/** True when the account holds a staff role, in whichever role it is browsing. */
export function canSeeBillcom(roles: readonly string[] | null | undefined): boolean {
  return Boolean(roles?.some((r) => r === 'trfer' || r === 'admin'));
}

// ── The ledger — status as the family reads it (spec §4.2) ───────────────

export type LedgerStatusKey =
  | 'needs_receipt' | 'scanning' | 'needs_details'
  | 'review' | 'needs_info' | 'approved' | 'arranging' | 'paying' | 'paid' | 'rejected';

/** `labelKey` resolves at render (#2109 R3-B4); no staff file reads these tokens. */
export interface LedgerStatusToken { key: LedgerStatusKey; labelKey: TKey; tone: ChipTone }

export const LEDGER_STATUS_KEYS: readonly LedgerStatusKey[] = [
  'needs_receipt', 'scanning', 'needs_details', 'review', 'needs_info',
  'approved', 'arranging', 'paying', 'paid', 'rejected',
];

export const LEDGER_STATUS_TOKENS: Record<LedgerStatusKey, LedgerStatusToken> = {
  needs_receipt: { key: 'needs_receipt', labelKey: 'money:ledgerStatus.needs_receipt', tone: 'warn' },
  scanning:      { key: 'scanning',      labelKey: 'money:ledgerStatus.scanning',      tone: 'neutral' },
  needs_details: { key: 'needs_details', labelKey: 'money:ledgerStatus.needs_details', tone: 'warn' },
  review:        { key: 'review',        labelKey: 'money:ledgerStatus.review',        tone: 'warn' },
  needs_info:    { key: 'needs_info',    labelKey: 'money:ledgerStatus.needs_info',    tone: 'warn' },
  approved:      { key: 'approved',      labelKey: 'money:ledgerStatus.approved',      tone: 'ok' },
  // D3 (#419 spec §5.2): approved, `payout.method === 'billcom'`, no paid_at —
  // an attempt stalled. Calm and TRUE: TRF still owes it, nothing is needed.
  arranging:     { key: 'arranging',     labelKey: 'money:ledgerStatus.arranging',     tone: 'neutral' },
  paying:        { key: 'paying',        labelKey: 'money:ledgerStatus.paying',        tone: 'ok' },
  paid:          { key: 'paid',          labelKey: 'money:ledgerStatus.paid',          tone: 'neutral' },
  rejected:      { key: 'rejected',      labelKey: 'money:ledgerStatus.rejected',      tone: 'danger' },
};

/** How long a fresh upload may read "Scanning…" before the row asks for the details. */
export const SCANNING_WINDOW_MS = 60_000;

/** The scan has not answered yet, and the upload is recent enough to keep waiting. */
export function isScanning(claim: ReimbursementClaim, now: Date = new Date()): boolean {
  if (claim.status !== 'draft' || !claim.source_document_id) return false;
  const ocr = claim.ocr_extracted;
  if (ocr?.extracted_at || ocr?.error) return false;
  const started = parseISO(claim.updated_at ?? claim.created_at);
  if (Number.isNaN(started.getTime())) return false;
  return now.getTime() - started.getTime() < SCANNING_WINDOW_MS;
}

/** True once the scan has run and could not read the receipt. */
export function scanFailed(claim: ReimbursementClaim): boolean {
  return Boolean(claim.source_document_id && claim.ocr_extracted?.error);
}

export function ledgerStatus(claim: ReimbursementClaim, now: Date = new Date()): LedgerStatusToken {
  switch (claim.status as ClaimStatus) {
    case 'draft':
      if (!claim.source_document_id) return LEDGER_STATUS_TOKENS.needs_receipt;
      if (isScanning(claim, now)) return LEDGER_STATUS_TOKENS.scanning;
      return LEDGER_STATUS_TOKENS.needs_details;
    case 'submitted':
    case 'under_review':
      return LEDGER_STATUS_TOKENS.review;
    case 'needs_info':
      return LEDGER_STATUS_TOKENS.needs_info;
    case 'approved':
      return claim.payout?.method === 'billcom' && !claim.payout?.paid_at
        ? LEDGER_STATUS_TOKENS.arranging
        : LEDGER_STATUS_TOKENS.approved;
    case 'paying':
      return LEDGER_STATUS_TOKENS.paying;
    case 'paid':
      return LEDGER_STATUS_TOKENS.paid;
    case 'rejected':
      return LEDGER_STATUS_TOKENS.rejected;
    default:
      return LEDGER_STATUS_TOKENS.review;
  }
}

/** The warn states that are the FAMILY's to act on — the "Needs your attention" group. */
const ATTENTION_KEYS: LedgerStatusKey[] = ['needs_receipt', 'scanning', 'needs_details', 'needs_info'];

export function needsAttention(claim: ReimbursementClaim, now: Date = new Date()): boolean {
  return ATTENTION_KEYS.includes(ledgerStatus(claim, now).key);
}

/**
 * With TRF and not yet decided: sent in (`submitted` / `under_review`, or a
 * status this client does not know, which `ledgerStatus` reads as review).
 * Nothing is the family's to do, and nothing is settled either (#2658).
 */
export function isWithTrf(claim: ReimbursementClaim, now: Date = new Date()): boolean {
  return ledgerStatus(claim, now).key === 'review';
}

/**
 * Which /bills receipts section a claim is filed under (#2658).
 *
 *   `todo`      the family's to act on — linked to a grant or not.
 *   `with_trf`  unlinked, sent in, not yet decided ("With TRF").
 *   `past`      unlinked and decided — approved, being paid, paid back, not
 *               approved ("Past receipts").
 *   `null`      linked and not the family's to act on: read inside its grant.
 *
 * Before #2658 `past` also held receipts still under review, so a receipt
 * TRF had not decided was filed as history.
 */
export type ReceiptSlice = 'todo' | 'with_trf' | 'past';

export function receiptSlice(claim: ReimbursementClaim, now: Date = new Date()): ReceiptSlice | null {
  if (needsAttention(claim, now)) return 'todo';
  if (claim.linked_to_grant) return null;
  return isWithTrf(claim, now) ? 'with_trf' : 'past';
}

// ── Dates and grouping (spec §4.3) ───────────────────────────────────────

/** The date a row is filed under: the expense date, or when it was recorded. */
export function claimDate(claim: ReimbursementClaim): Date | null {
  // `expense_date` is date-only (#1308): the backend stores `new Date('2026-09-04')`,
  // UTC midnight, which every reader west of UTC would otherwise show a day early.
  if (claim.expense_date) return parseDateOnly(claim.expense_date);
  if (!claim.created_at) return null;
  const d = parseISO(claim.created_at);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The day a receipt's row prints: `Sep 4` this year, `Sep 4, 2025` otherwise,
 * in the reader's locale. Null when the receipt carries no `expense_date`.
 *
 * #2491: one rule for every receipts list (all of them render `ExpenseRow`).
 * `expense_date` is a calendar day (#1308), so it is read for its UTC calendar
 * parts and formatted in UTC: the day that was typed or scanned, in every
 * zone. A receipt with no `expense_date` prints NO date — the upload instant
 * (`created_at`) is when the photo arrived, not when the money was spent, and
 * the details dialog's "When" field is empty for it. `groupClaimsByMonth`
 * still files such a receipt under its upload month through `claimDate`.
 *
 * `timeZone` is the reader's zone (default: the runtime's), used only to
 * decide which year "this year" is.
 */
export function claimDateLabel(
  claim: ReimbursementClaim,
  now: Date = new Date(),
  options: { locale?: string; timeZone?: string } = {},
): string | null {
  const day = parseDateOnly(claim.expense_date);
  if (!day) return null;
  const utcDay = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()));
  const nowYear = formatPreset(now, { year: 'numeric' }, { locale: 'en', timeZone: options.timeZone });
  const sameYear = String(day.getFullYear()) === nowYear;
  return formatPreset(utcDay, sameYear ? 'monthDay' : 'monthDayYear', { locale: options.locale, dateOnly: true });
}

export interface LedgerGroup {
  /** `attention`, `undated`, or the month as `YYYY-MM`. Resolve the heading with `ledgerGroupLabel`. */
  key: string;
  items: ReimbursementClaim[];
  /** Sum of the month's known amounts; null when a row has no amount yet. Attention carries none. */
  total: number | null;
}

/**
 * Needs your attention first, then one group per calendar month, newest
 * month first, newest row first inside a month. A month made only of paid
 * rows is still shown — a statement shows what was paid. A month's total
 * leaves out the receipts TRF did not approve (#2381).
 */
export function groupClaimsByMonth(claims: ReimbursementClaim[], now: Date = new Date()): LedgerGroup[] {
  const attention: ReimbursementClaim[] = [];
  const byMonth = new Map<string, ReimbursementClaim[]>();
  for (const c of claims) {
    if (needsAttention(c, now)) { attention.push(c); continue; }
    const d = claimDate(c);
    const key = d ? format(d, 'yyyy-MM') : 'undated';
    const list = byMonth.get(key) ?? [];
    list.push(c);
    byMonth.set(key, list);
  }
  const byNewest = (a: ReimbursementClaim, b: ReimbursementClaim) =>
    (claimDate(b)?.getTime() ?? 0) - (claimDate(a)?.getTime() ?? 0);

  const groups: LedgerGroup[] = [];
  if (attention.length > 0) {
    groups.push({ key: 'attention', items: attention.sort(byNewest), total: null });
  }
  const monthKeys = [...byMonth.keys()].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  for (const key of monthKeys) {
    const items = (byMonth.get(key) ?? []).sort(byNewest);
    // #2381: a receipt TRF did not approve is money nobody will pay, so it
    // is listed in its month but never counted in the month's total. A month
    // of nothing but rejections has no total at all rather than "$0.00".
    const counted = items.filter((c) => c.status !== 'rejected');
    const total = counted.length > 0 && counted.every((c) => c.amount != null && Number.isFinite(Number(c.amount)))
      ? counted.reduce((sum, c) => sum + Number(c.amount), 0)
      : null;
    groups.push({ key, items, total });
  }
  return groups;
}

/**
 * A group's heading in the reader's language (#2109 R3-B3): the builder
 * returns only the key, so a language switch re-renders the heading. The
 * month name goes through Intl ("September 2026", "septiembre de 2026").
 */
export function ledgerGroupLabel(
  group: Pick<LedgerGroup, 'key'>,
  t: AnyTFunction = i18n.t,
  options: { locale?: string } = {},
): string {
  const tt = t as unknown as LooseT;
  if (group.key === 'attention') return tt('money:ledger.attention');
  if (group.key === 'undated') return tt('money:ledger.noDate');
  const m = /^(\d{4})-(\d{2})$/.exec(group.key);
  if (!m) return group.key;
  return formatMonthYear(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)), { ...options, dateOnly: true });
}

// ── Toolbar filters ───────────────────────────────────────────────────────
//
// One object for every section so switching tabs keeps each section's
// choice. BillsPage owns the state; FinancesChrome renders and edits it.

/** `attention` = the family's own warn states; `review` = with TRF; `paid` = paid back. */
export type ClaimScope = 'all' | 'attention' | 'review' | 'paid';
export type CategoryScope = 'all' | ExpenseCategory;
/** #2821: the statuses a family reads, so `awarded` is a scope of its own. */
export type GrantScope = 'all' | FamilyGrantStatus;

export interface FinancesFilters {
  claimScope: ClaimScope;
  categoryScope: CategoryScope;
  grantScope: GrantScope;
}

export const DEFAULT_FINANCES_FILTERS: FinancesFilters = {
  claimScope: 'all',
  categoryScope: 'all',
  grantScope: 'all',
};

const REVIEW_CLAIM_STATUSES: ClaimStatus[] = ['submitted', 'under_review', 'approved', 'paying'];

/**
 * True when anything is filtered off its default — drives the dot on the
 * toolbar icon.
 *
 * Lost its `section` argument on 2026-09-21: with one page there is one
 * filter popover, and every field in it narrows the same list.
 */
export function isFinancesFiltered(f: FinancesFilters): boolean {
  return f.grantScope !== DEFAULT_FINANCES_FILTERS.grantScope
    || f.claimScope !== DEFAULT_FINANCES_FILTERS.claimScope
    || f.categoryScope !== DEFAULT_FINANCES_FILTERS.categoryScope;
}

// ── Toolbar state in the URL (#2407) ─────────────────────────────────────
//
// The filter and the search live in the query string, so a filtered /bills
// survives a reload, a shared link and the back button. Every param is
// validated against its own vocabulary; a value the page does not know is
// the default, never an empty list. Params the page does not own (the
// historical `?tab=`) are left exactly as they came.

const CLAIM_SCOPES: readonly ClaimScope[] = ['all', 'attention', 'review', 'paid'];

/** The query-string names, one per toolbar field. */
const PARAM = { search: 'q', grantScope: 'grants', claimScope: 'receipts', categoryScope: 'category' } as const;

function pick<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

export function financesStateFromParams(params: URLSearchParams): { filters: FinancesFilters; search: string } {
  return {
    filters: {
      grantScope: pick<GrantScope>(params.get(PARAM.grantScope), ['all', ...FAMILY_GRANT_STATUSES], DEFAULT_FINANCES_FILTERS.grantScope),
      claimScope: pick<ClaimScope>(params.get(PARAM.claimScope), CLAIM_SCOPES, DEFAULT_FINANCES_FILTERS.claimScope),
      categoryScope: pick<CategoryScope>(params.get(PARAM.categoryScope), ['all', ...EXPENSE_CATEGORIES], DEFAULT_FINANCES_FILTERS.categoryScope),
    },
    search: params.get(PARAM.search) ?? '',
  };
}

/** A copy of `params` holding `filters` and `search`; a field at its default is removed. */
export function writeFinancesState(params: URLSearchParams, filters: FinancesFilters, search: string): URLSearchParams {
  const next = new URLSearchParams(params);
  const set = (name: string, value: string, fallback: string) => {
    if (value === fallback) next.delete(name);
    else next.set(name, value);
  };
  set(PARAM.search, search, '');
  set(PARAM.grantScope, filters.grantScope, DEFAULT_FINANCES_FILTERS.grantScope);
  set(PARAM.claimScope, filters.claimScope, DEFAULT_FINANCES_FILTERS.claimScope);
  set(PARAM.categoryScope, filters.categoryScope, DEFAULT_FINANCES_FILTERS.categoryScope);
  return next;
}

// ── Toolbar search (client-side, per section) ─────────────────────────────
//
// Fields are the ones a parent would type from memory — the merchant, the
// note, the category as it is LABELLED, the amount — never ids or enum keys.

function hay(...parts: (string | number | undefined | null)[]): string {
  return parts.filter((p) => p !== undefined && p !== null && p !== '').map(String).join(' ').toLowerCase();
}

function norm(query: string): string {
  return query.trim().toLowerCase();
}

function claimInScope(c: ReimbursementClaim, scope: ClaimScope, now: Date): boolean {
  if (scope === 'all') return true;
  if (scope === 'attention') return needsAttention(c, now);
  if (scope === 'review') return REVIEW_CLAIM_STATUSES.includes(c.status);
  return c.status === 'paid';
}

export function claimMatches(
  c: ReimbursementClaim,
  scope: ClaimScope,
  category: CategoryScope,
  query: string,
  now: Date = new Date(),
  t: AnyTFunction = i18n.t,
): boolean {
  if (!claimInScope(c, scope, now)) return false;
  if (category !== 'all' && c.category !== category) return false;
  const q = norm(query);
  if (!q) return true;
  // The category as the reader sees it (#2109 R3-B4): "Alquiler" finds rent.
  return hay(c.vendor, c.note, expenseCategoryLabel(c.category, t), c.amount).includes(q);
}

/** The grants panel's receipt picker still reads the lite shape. */
/**
 * A general receipt the family may attach to a grant: not a draft, not
 * rejected, not already linked — and not already approved or paid (the
 * server refuses to move a settled claim between grants with a 409 since
 * 2026-09-19, so the picker never offers one).
 */
export function isLinkableClaim(c: ReimbursementClaimLite): boolean {
  return c.kind === 'general' && !c.linked_to_grant
    && !['rejected', 'draft', 'approved', 'paying', 'paid'].includes(c.status);
}

export function grantMatches(g: GrantRequest, scope: GrantScope, query: string): boolean {
  // #2821: the scope and the search read the family's word (`awarded` for a
  // Signature grant TRF created), the same one the row prints.
  if (scope !== 'all' && familyGrantStatus(g) !== scope) return false;
  const q = norm(query);
  if (!q) return true;
  // The labels the reader sees (#2109 R3-B4), in the active language. The
  // expense is the row's own label (#2393), never the staff category_detail.
  return hay(grantTypeLabel(g.type), familyGrantStatusLabel(g), grantExpenseLabel(g)).includes(q);
}

// ── The expense a grant covers (#2393) ───────────────────────────────────

/** One catalog key per option of the wizard's `expense_category` select. */
const GRANT_EXPENSE_KEYS: Record<GrantExpenseCategory, string> = {
  rent_mortgage: 'grants:expenseCategory.rent_mortgage',
  car_payment: 'grants:expenseCategory.car_payment',
  utilities: 'grants:expenseCategory.utilities',
  travel_mileage: 'grants:expenseCategory.travel_mileage',
  travel_lodging: 'grants:expenseCategory.travel_lodging',
  travel_meals: 'grants:expenseCategory.travel_meals',
  other: 'grants:expenseCategory.other',
};

/**
 * The expense a family's grant covers, in the reader's language: "Rent or
 * mortgage", or "Other: <the family's own words>". Null when the grant
 * carries no category the platform knows, which includes every grant
 * materialised before #2393 (there is no backfill).
 *
 * Before #2393 the family's row printed `category_detail`, the staff record
 * of the ask ("Family asked for USD 350 — rent_mortgage — Account holder:
 * …"), in English with a raw enum. That field no longer reaches the family.
 */
export function grantExpenseLabel(
  grant: Pick<GrantRequest, 'expense_category' | 'expense_category_other'>,
  t: AnyTFunction = i18n.t,
): string | null {
  const category = grant.expense_category;
  if (!category || !Object.prototype.hasOwnProperty.call(GRANT_EXPENSE_KEYS, category)) return null;
  const tt = t as unknown as LooseT;
  const own = category === 'other' ? grant.expense_category_other?.trim() : '';
  return own
    ? tt('grants:expenseCategory.otherWithDetail', { detail: own })
    : tt(GRANT_EXPENSE_KEYS[category]);
}

/**
 * #2530: the staff record string `category_detail` ("Family asked for USD 350
 * — rent_mortgage — Creditor: …") with every segment that is a raw category
 * key replaced by its label ("Rent or mortgage"), through the same map the
 * family's grant row reads. New grants are written with the label already
 * (backend grantMaterialization); this keeps every grant written before that
 * readable without a migration. Null for an empty string.
 */
export function staffGrantPurpose(
  detail: string | null | undefined,
  t: AnyTFunction = i18n.t,
): string | null {
  const text = detail?.trim();
  if (!text) return null;
  return text
    .split(' — ')
    .map((segment) => (Object.prototype.hasOwnProperty.call(GRANT_EXPENSE_KEYS, segment)
      ? grantExpenseLabel({ expense_category: segment as GrantExpenseCategory, expense_category_other: null }, t) ?? segment
      : segment))
    .join(' — ');
}
